<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faArrowRight, faTrashAlt, faSave, faUser } from '@fortawesome/free-solid-svg-icons'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import ZsheetSection from '../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetAdd from '../../_common/z/sheet/zsheet-add.svelte'
    import { TaxInvoiceEntity } from '../../../modules/posApi/tax-invoices/tax-invoice.entity'
    import TaxFormItems from './tax-form-items.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import type { TaxParamEntity } from '../../../modules/posApi/tax-params/tax-param.entity'
    import { TaxParamNfseIssuerEnum } from '../../../modules/posApi/tax-params/tax-param.enum'
    import type { Invoice } from '../../../modules/posApi/invoices/invoice'

    const dispatch = createEventDispatcher()

    export let taxInvoiceId: string = null
    export let invoice: Invoice = null

    let show = false
    let entity: TaxInvoiceEntity = null
    let newService: boolean = false
    let taxParams: TaxParamEntity

    let isDisabled = false

    // ISS retido (emissor nacional): quem retém é o tomador pessoa jurídica (LC 116, art. 6º). Com CPF ou
    // estrangeiro a nota vai sempre como não retido e o campo nem aparece; com CNPJ aparece já com o padrão
    // dos parâmetros. O backend aplica a mesma regra na emissão, com o documento atual do cliente.
    const issRetidoOptions = [
        { label: 'Não retido', value: 1 },
        { label: 'Retido pelo tomador', value: 2 },
    ]
    $: isNacional = taxParams?.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL
    $: tomadorPJ = (entity?.person?.document || '').replace(/\D/g, '').length === 14
    // função (e não um bloco $: que lê entity) para não criar ciclo: o bloco depende só dos dois sinalizadores
    $: applyISSRetidoDefault(isNacional, tomadorPJ)
    function applyISSRetidoDefault(nacional: boolean, pj: boolean) {
        if (!nacional || !pj || !entity || entity.nfseISSRetido) return
        entity.nfseISSRetido = taxParams.nfseDefaultServiceISSRetido === 2 ? 2 : 1
    }

    // sem alíquota configurada a transmissão com ISS retido é recusada pelo pos-api: o aviso já aparece aqui
    $: issRetidoAliquota =
        taxParams?.nfseDefaultServiceTaxRate > 0
            ? taxParams.nfseDefaultServiceTaxRate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %'
            : 'Não configurada nos parâmetros fiscais'

    function issRetidoForDto(): number {
        if (!isNacional) return undefined
        return tomadorPJ ? entity.nfseISSRetido : 1
    }

    onMount(async () => {
        taxParams = await window.api.pos.taxParams.getOne(window.store.company.companyId)
        if (taxInvoiceId) {
            await loadEntity(taxInvoiceId)
        } else {
            newEntity()
            if (invoice) {
                await importInvoice()
            }
        }
    })

    async function importInvoice() {
        entity.personId = invoice.personId
        // a função já grava entity.person e devolve o próprio entity: reatribuir o retorno fazia
        // entity.person virar o entity, e o cliente chegava como "undefined" no formulário
        await window.api.utils.utils.assignPersonsFromObject(entity)
        entity.services = await window.api.utils.utils.assignServicesFromObject(entity.services)
        for (const service of invoice.services) {
            if (service.service.type !== 'service') continue
            if (service.value <= 0) continue
            entity.services.push({
                cnae: taxParams.nfseDefaultServiceCNAE,
                code: taxParams.nfseDefaultServiceCode,
                taxCode: taxParams.nfseDefaultServiceTaxCode,
                description: service.service.name,
                iss: { taxRate: taxParams.nfseDefaultServiceTaxRate, taxSituation: taxParams.nfseDefaultServiceTaxSituation },
                value: { grossValue: Math.round(service.value * service.quantity * 100) / 100 },
                serviceId: service.serviceId,
                service: service.service,
                nbs: taxParams.nfseDefaultServiceNBS,
                codigoContribuinte: taxParams.nfseDefaultServiceCodigoContribuinte,
                codigoCidadeIncidencia: taxParams.nfseDefaultServiceCodigoCidadeIncidencia,
                codigoServicoNacional: taxParams.nfseDefaultServiceCodigoServicoNacional,
                ibscbs: taxParams.nfseDefaultServiceIBSCBS,
            })
        }
        entity = entity
    }

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new TaxInvoiceEntity()
            entity = await window.api.pos.taxInvoices.getOne(id)
            if (entity.personId) {
                entity.person = await window.api.events.persons.getOne(entity.personId)
            }
            entity.services = await window.api.utils.utils.assignServicesFromObject(entity.services)
            isDisabled = !entity.taxInvoiceId
            isDisabled = entity.status !== 97 && entity.status !== 100
            console.log('taxInvoice', entity)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        entity = new TaxInvoiceEntity()
        entity.services = []
    }

    async function saveEntity(transmit: boolean) {
        if (entity.taxInvoiceId) {
            updateEntity(transmit)
        } else {
            createEntity(transmit)
        }
    }

    async function createEntity(transmit: boolean) {
        try {
            if (show) return
            show = true
            // limpa itens nulos
            entity.services = entity.services.filter((el) => !!el)
            const dto: Partial<TaxInvoiceEntity> = {
                taxInvoiceId: entity.taxInvoiceId || undefined,
                personId: entity.person?.personId,
                services: entity.services
                    .filter((el) => !!el)
                    .map((el) => {
                        return {
                            cnae: el.cnae,
                            code: el.code,
                            description: el.description,
                            iss: el.iss,
                            taxCode: el.taxCode,
                            value: el.value,
                            serviceId: el.serviceId,
                            nbs: el.nbs,
                            codigoContribuinte: el.codigoContribuinte,
                            codigoCidadeIncidencia: el.codigoCidadeIncidencia,
                            codigoServicoNacional: el.codigoServicoNacional,
                            ibscbs: el.ibscbs,
                        }
                    }),
                nfseISSRetido: issRetidoForDto(),
            }

            if (!validateDto(dto)) return false
            const res = await window.api.pos.taxInvoices.create(dto)
            if (res) {
                res.person = entity.person
                entity = res
            }
            if (invoice) {
                window.api.pos.invoices.setTaxInvoiceId(invoice.invoiceId, res.taxInvoiceId)
            }
            console.log('create-taxInvoice', res)
            if (transmit) {
                await window.api.pos.taxInvoices.sendNFSe(res.taxInvoiceId)
            }
            dispatch('change')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function updateEntity(transmit: boolean) {
        try {
            if (show) return
            show = true
            // limpa itens nulos
            entity.services = entity.services.filter((el) => !!el)
            const dto: Partial<TaxInvoiceEntity> = {
                taxInvoiceId: entity.taxInvoiceId || undefined,
                personId: entity.person?.personId,
                services: entity.services
                    .filter((el) => !!el)
                    .map((el) => {
                        return {
                            cnae: el.cnae,
                            code: el.code,
                            description: el.description,
                            iss: el.iss,
                            taxCode: el.taxCode,
                            value: el.value,
                            serviceId: el.serviceId,
                            nbs: el.nbs,
                            codigoContribuinte: el.codigoContribuinte,
                            codigoCidadeIncidencia: el.codigoCidadeIncidencia,
                            codigoServicoNacional: el.codigoServicoNacional,
                            ibscbs: el.ibscbs,
                        }
                    }),
                nfseISSRetido: issRetidoForDto(),
            }

            if (!validateDto(dto)) return false
            const res = await window.api.pos.taxInvoices.update(entity.taxInvoiceId, dto)
            console.log('update-taxInvoice', res)
            if (transmit && (res.status === 100 || res.status === 97)) {
                // 100 = rascunho // 97 = rejeitada
                await window.api.pos.taxInvoices.sendNFSe(res.taxInvoiceId)
            }
            dispatch('change')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto: Partial<TaxInvoiceEntity>) {
        if (!dto.personId) {
            window.error('Informe o cliente/tomador.')
            return false
        }
        if (dto.services.length === 0) {
            window.error('Você deve informar ao menos um serviço')
            return false
        }
        if (dto.services.length > 0 && dto.services.reduce((acc, el) => (acc += el.value.grossValue), 0) < 0) {
            window.error('Informe um valor válido para todos os serviços')
            return false
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.pos.taxInvoices.remove(entity.taxInvoiceId)
            dispatch('change')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function newServiceItem() {
        newService = true
    }

    async function serviceAdded(event) {
        console.log('added', event.detail)

        entity.services.push(event.detail)
        entity.services = entity.services
        newService = false
    }

    function serviceClosed() {
        newService = false
        entity.services = entity.services.filter((el) => !!el)
    }

    function showEntities() {
        dispatch('cancel')
    }

    // ---- apresentação -----------------------------------------------------------------------
    const money = (v: number) => window.api.utils.utils.toReal(v || 0)

    $: total = (entity?.services || []).reduce((acc, el) => acc + (el?.value?.grossValue || 0), 0)

    function personInitials(name: string) {
        const words = (name || '').trim().split(/\s+/).filter(Boolean)
        if (words.length === 0) return ''
        if (words.length === 1) return words[0].substring(0, 2)
        return words[0][0] + words[words.length - 1][0]
    }

    // os códigos fiscais viram uma linha de apoio: cada um sozinho não diz nada, juntos identificam
    // a tributação do item — e assim a lista deixa de precisar de um cabeçalho de tabela
    // No emissor nacional esses códigos por item não têm efeito (a nota usa os dos parâmetros fiscais).
    function itemSubtitle(item) {
        if (taxParams?.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL) return ''
        const partes = []
        if (item.code) partes.push('Cód. ' + item.code)
        if (item.taxCode) partes.push('Trib. ' + item.taxCode)
        if (item.cnae) partes.push('CNAE ' + item.cnae)
        if (item.iss?.taxRate || item.iss?.taxRate === 0) partes.push('ISS ' + item.iss.taxRate + '%')
        return partes.join(' · ')
    }

    let editingItem: number = null
    function openItem(index: number) {
        if (isDisabled) return
        newService = false
        editingItem = index
    }
    function itemFinished() {
        editingItem = null
        entity.services = entity.services.filter((el) => !!el)
    }
</script>

<LoadSpinner delay={0} {show}></LoadSpinner>

{#if entity}
    {#if !taxParams}
        <div class="m-5">
            <div class="alert alert-warning text-center">
                Para emitir notas fiscais é necessário primeiro configurar o Zandu para emissão.<br />
                Para realizar a configuração vá até o menu Ferramentas &gt; Configurações de Notas Fiscais.<br />
                O módulo de notas fiscais precisa estar contratado. Em caso de dúvidas entre em contato com o nosso suporte.
            </div>
        </div>
    {:else}
        <div class="root m-bottom-sm">
            <Zact>
                {#if window.store.token.permissions.pos.taxInvoices.write}
                    <div class="spacer"></div>
                    {#if !!entity.taxInvoiceId}
                        <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={isDisabled} on:click={removeEntity} />
                    {/if}
                    <Zbutton color="secondary" icon={faSave} label="Salvar como rascunho" disabled={isDisabled} on:click={() => saveEntity(false)} />
                    <Zbutton color="primary" icon={faArrowRight} label="Transmitir" disabled={isDisabled} on:click={() => saveEntity(true)} />
                {/if}
            </Zact>

            <div class="sections">
                <ZsheetSection label="Cliente" meta={entity.person?.personId ? '' : 'obrigatório'}>
                    <!-- personId, e não person: ao tocar em "+" para cadastrar, o seletor preenche uma
                         pessoa em branco (ainda sem id) e abre o cadastro dentro dele -->
                    {#if entity.person?.personId}
                        <ZsheetEntity
                            initials={personInitials(entity.person.name)}
                            icon={faUser}
                            title={entity.person.name}
                            subtitle={entity.person.phone ? window.api.utils.utils.phoneFriendly(entity.person.phone) : ''}
                            removable={!isDisabled}
                            removeTitle="Trocar cliente"
                            on:remove={() => (entity.person = null)}
                        />
                    {:else}
                        <ZentitySelect fonte="pessoa" label="Buscar por nome, telefone ou e-mail" bind:value={entity.person} />
                    {/if}
                    {#if isNacional && tomadorPJ}
                        <ZContainer class="iss-retido">
                            <Zinput
                                size={6}
                                sizeM={12}
                                label="ISS"
                                type="inlineselect_2"
                                options={issRetidoOptions}
                                disabled={isDisabled}
                                help="Cliente pessoa jurídica pode ser obrigado a reter o ISS, pela LC 116 ou pela lei do município dele. O padrão vem dos parâmetros fiscais. Para cliente pessoa física a nota vai sempre como não retido."
                                bind:value={entity.nfseISSRetido}
                            />
                            {#if entity.nfseISSRetido === 2}
                                <!-- só leitura: a alíquota vai na nota quando o ISS é retido, e vem dos parâmetros -->
                                <Zinput
                                    size={6}
                                    sizeM={12}
                                    label="Alíquota do ISS retido"
                                    type="text"
                                    disabled={true}
                                    help="É sobre esta alíquota que o cliente retém o ISS. Ela vem do campo 'Alíquota do ISS' dos parâmetros fiscais; para alterar, ajuste lá. Para empresa do Simples Nacional, deve ser a alíquota do ISS da faixa em que a empresa está."
                                    value={issRetidoAliquota}
                                />
                            {/if}
                        </ZContainer>
                    {/if}
                </ZsheetSection>

                <ZsheetSection label="Serviços na nota" meta={entity.services?.length ? money(total) : 'obrigatório'}>
                    <div class="list">
                        {#each entity.services || [] as item, index}
                            {#if item}
                                {#if editingItem === index}
                                    <div class="edit">
                                        <TaxFormItems
                                            {taxParams}
                                            startEditing={true}
                                            bind:item={entity.services[index]}
                                            on:cancel={itemFinished}
                                            on:added={itemFinished}
                                        />
                                    </div>
                                {:else}
                                    <ZsheetEntity
                                        initials={(item.description || '?').substring(0, 1)}
                                        title={item.description || '[serviço sem descrição]'}
                                        subtitle={itemSubtitle(item)}
                                        clickable={!isDisabled}
                                        on:click={() => openItem(index)}
                                    >
                                        <svelte:fragment slot="trailing">
                                            <span class="amount">{money(item.value?.grossValue)}</span>
                                        </svelte:fragment>
                                    </ZsheetEntity>
                                {/if}
                            {/if}
                        {/each}

                        {#if newService}
                            <div class="edit">
                                <TaxFormItems {taxParams} on:cancel={serviceClosed} on:added={serviceAdded} />
                            </div>
                        {:else if !isDisabled}
                            <ZsheetAdd label="Serviço" on:click={newServiceItem} />
                        {/if}
                    </div>
                </ZsheetSection>
            </div>
        </div>
    {/if}
{/if}

<style>
    /* pilha de blocos, a mesma da folha do mobile: cada assunto num cartão, separados por um vão */
    .sections {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: 12px 8px;
    }
    .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    /* o editor do item abre no lugar da linha, com o mesmo fundo do bloco em volta */
    .edit {
        border: 1px solid var(--z-field-border);
        border-radius: var(--z-field-radius);
        padding: 8px;
    }
    /* global: a classe vai na raiz do ZContainer, que é desenhada por outro componente */
    :global(.iss-retido) {
        margin-top: 8px;
    }
    .amount {
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
    }

    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
    }
</style>
